<template>
  <div class="notification" @mouseenter="openDropdown" @mouseleave="startCloseTimer">
    <router-link v-if="isMobile" to="/notifications" class="mobile-notification-link">
      <i class="pi pi-bell"></i>
      <span>Уведомления</span>
      <span v-if="unreadCount > 0" class="badge">{{ unreadCount > 99 ? "99+" : unreadCount }}</span>
    </router-link>

    <div v-else class="notification-desktop">
      <button type="button" class="bell" ref="bellRef">
        <i class="pi pi-bell"></i>
        <span v-if="unreadCount > 0" class="badge">
          {{ unreadCount > 99 ? "99+" : unreadCount }}
        </span>
      </button>

      <Teleport to="body">
        <Transition name="dropdown-fade">
          <div v-if="isOpen" class="notification-dropdown" :style="dropdownStyle" @mouseenter="cancelCloseTimer"
            @mouseleave="startCloseTimer">
            <div class="dropdown-header">
              <span class="dropdown-title">Уведомления</span>
              <button v-if="unreadCount > 0" class="mark-all-btn" @click.stop="handleMarkAllAsRead">
                <i class="pi pi-check-double"></i>
                Прочитать все
              </button>
            </div>

            <div v-if="loading" class="dropdown-loading">
              <i class="pi pi-spin pi-spinner"></i>
              <p>Загрузка...</p>
            </div>

            <div v-else-if="notifications.length === 0" class="dropdown-empty">
              <i class="pi pi-bell-slash"></i>
              <p>Нет уведомлений</p>
            </div>

            <div v-else class="dropdown-list">
              <div v-for="notification in notifications.slice(0, 10)" :key="notification.id" class="dropdown-item"
                :class="{ unread: !notification.isRead }" @click="onNotificationClick(notification)">
                <div class="item-icon">
                  <i :class="getIcon(notification.title)"></i>
                </div>
                <div class="item-content">
                  <div class="item-title">{{ notification.title }}</div>
                  <div class="item-message">{{ notification.message }}</div>
                  <div class="item-time">
                    {{ formatTime(notification.createdAt) }}
                  </div>

                  <button v-if="
                    canAssignOrder &&
                    notification.title?.includes('новый заказ')
                  " class="assign-btn" @click.stop="handleAssignOrder(notification)">
                    <i class="pi pi-user-plus"></i>
                    Взять в работу
                  </button>
                </div>
                <div v-if="!notification.isRead" class="item-unread-dot"></div>
              </div>
            </div>

            <div v-if="notifications.length > 0" class="dropdown-footer">
              <router-link to="/notifications" class="view-all-link" @click="closeDropdown">
                Все уведомления
                <i class="pi pi-arrow-right"></i>
              </router-link>
            </div>
          </div>
        </Transition>
      </Teleport>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/modules/auth/store/auth.store";
import { usePermissionsStore } from "@/modules/auth/store/permissions.store";
import { useNotificationStore } from "../store/notification.store";
import { NotificationTargetType } from "../model/notification-target";
import { http } from "@/services/http";
import { useToast } from "primevue/usetoast";
import { useMediaQuery } from '@/shared/composables/useMediaQuery'

const router = useRouter();
const authStore = useAuthStore();
const permissionsStore = usePermissionsStore();
const notificationStore = useNotificationStore();
const toast = useToast();
const isMobile = useMediaQuery('(max-width: 1400px)')

const isOpen = ref(false);
const bellRef = ref(null);
const dropdownPosition = ref({ top: 0, right: 0 });
let closeTimer = null;

const dropdownStyle = computed(() => ({
  position: "fixed",
  top: dropdownPosition.value.top + "px",
  right: dropdownPosition.value.right + "px",
  width: "380px"
}));

const updateDropdownPosition = () => {
  if (bellRef.value) {
    const rect = bellRef.value.getBoundingClientRect();
    dropdownPosition.value = {
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right
    };
  }
};

const notifications = computed(() => notificationStore.notifications);
const unreadCount = computed(() => notificationStore.unreadCount);
const loading = computed(() => notificationStore.loading);

const canAssignOrder = computed(() => {
  return permissionsStore.hasAnyPermission(["AssignOrder"]);
});

const openDropdown = () => {
  cancelCloseTimer();
  updateDropdownPosition();
  isOpen.value = true;
  if (notifications.value.length === 0) {
    notificationStore.fetchNotifications();
  }
};

const closeDropdown = () => {
  isOpen.value = false;
};

const startCloseTimer = () => {
  closeTimer = setTimeout(() => {
    isOpen.value = false;
  }, 200);
};

const cancelCloseTimer = () => {
  if (closeTimer) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }
};

const handleMarkAllAsRead = async () => {
  await notificationStore.markAllAsRead();
};

const handleAssignOrder = async (notification) => {
  try {
    const response = await http.post(`/orders/${notification.orderId}/assign`);

    if (response.status === 200) {
      toast.add({
        severity: "success",
        summary: "Успешно",
        detail: `Заказ №${notification.orderId} назначен на вас`,
        life: 3000,
      });

      await notificationStore.fetchNotifications();
      closeDropdown();
      router.push(`/admin/orders/${notification.orderId}`);
    }
  } catch (error) {
    console.error("Ошибка при назначении заказа:", error);
    toast.add({
      severity: "error",
      summary: "Ошибка",
      detail: error.response?.data?.message || "Не удалось назначить заказ",
      life: 3000,
    });
  }
};

const onNotificationClick = async (notification) => {
  if (!notification.isRead) {
    await notificationStore.markAsRead(notification.id);
  }

  closeDropdown();

  if (notification.targetType === NotificationTargetType.RegistrationReview && notification.targetId) {
    router.push(`/admin/registrations/${encodeURIComponent(notification.targetId)}`);
    return;
  }

  if (notification.orderId) {
    const roles = authStore.user?.roles || [];
    const isAdminOrManager = roles.some((role) =>
      ["Admin", "Manager", "God"].includes(role),
    );

    if (isAdminOrManager) {
      router.push(`/admin/orders/${notification.orderId}`);
    } else {
      router.push({
        name: "orderDetails",
        params: { id: notification.orderId },
      });
    }
  }
};

const getIcon = (title) => {
  if (!title) return "pi pi-bell";
  if (title.includes("новый заказ") || title.includes("🛒"))
    return "pi pi-shopping-cart";
  if (title.includes("назначен")) return "pi pi-user-plus";
  if (title.includes("статус") || title.includes("🟡") || title.includes("🚚"))
    return "pi pi-refresh";
  if (title.includes("выполнен") || title.includes("✅"))
    return "pi pi-check-circle";
  if (title.includes("закрыт") || title.includes("❌"))
    return "pi pi-times-circle";
  return "pi pi-bell";
};

const formatTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;

  if (diff < 60 * 1000) return "только что";
  if (diff < 60 * 60 * 1000) return `${Math.floor(diff / 60000)} мин`;
  if (diff < 24 * 60 * 60 * 1000) return `${Math.floor(diff / 3600000)} ч`;
  if (diff < 7 * 24 * 60 * 60 * 1000)
    return `${Math.floor(diff / (24 * 3600000))} дн`;
  return date.toLocaleDateString("ru-RU");
};

let pollingInterval = null;

const startPolling = () => {
  if (pollingInterval) clearInterval(pollingInterval);
  pollingInterval = setInterval(() => {
    if (!document.hidden && isOpen.value) {
      notificationStore.fetchUnreadCount();
    }
  }, 30000);
};

onMounted(() => {
  startPolling();
  notificationStore.fetchUnreadCount();
  notificationStore.fetchNotifications();
  window.addEventListener("resize", updateDropdownPosition);
  window.addEventListener("scroll", updateDropdownPosition);
});

onUnmounted(() => {
  if (pollingInterval) clearInterval(pollingInterval);
  if (closeTimer) clearTimeout(closeTimer);
  window.removeEventListener("resize", updateDropdownPosition);
  window.removeEventListener("scroll", updateDropdownPosition);
});
</script>

<style scoped>
.notification {
  position: relative;
  display: inline-block;
}

.bell {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 1px solid #e1e1e1;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.bell i {
  color: #222222;
  font-size: 18px;
}

.bell:hover {
  background: #f6f6f6;
  border-color: #c41217;
}

.bell:hover i {
  color: #c41217;
}

.badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 50%;
  background: #c41217;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-dropdown {
  position: fixed;
  top: 70px;
  right: 20px;
  width: 380px;
  max-height: 500px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.dropdown-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #efefef;
  background: #ffffff;
}

.dropdown-title {
  font-size: 16px;
  font-weight: 700;
  color: #111111;
}

.mark-all-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: #c41217;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 20px;
  transition: background 0.2s;
}

.mark-all-btn:hover {
  background: #fff5f5;
}

.dropdown-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  gap: 12px;
  color: #999;
}

.dropdown-loading i {
  font-size: 32px;
}

.dropdown-empty {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

.dropdown-empty i {
  font-size: 40px;
  margin-bottom: 12px;
  opacity: 0.5;
}

.dropdown-empty p {
  margin: 0;
  font-size: 14px;
}

.dropdown-list {
  flex: 1;
  overflow-y: auto;
  max-height: 380px;
}

.dropdown-item {
  display: flex;
  gap: 12px;
  padding: 14px 20px;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.2s;
  position: relative;
  cursor: pointer;
}

.dropdown-item:hover {
  background: #fafafa;
}

.dropdown-item.unread {
  background: #fff9f9;
}

.dropdown-item.unread:hover {
  background: #fff5f5;
}

.item-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border-radius: 10px;
  color: #c41217;
  font-size: 16px;
}

.item-content {
  flex: 1;
  min-width: 0;
}

.item-title {
  font-weight: 600;
  font-size: 13px;
  color: #1d1d1d;
  margin-bottom: 4px;
}

.item-message {
  font-size: 12px;
  color: #666;
  line-height: 1.4;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-time {
  font-size: 10px;
  color: #999;
}

.assign-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 4px 12px;
  background: #c41217;
  border: none;
  border-radius: 20px;
  color: white;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.assign-btn:hover {
  background: #a00e12;
  transform: translateY(-1px);
}

.assign-btn i {
  font-size: 10px;
}

.item-unread-dot {
  position: absolute;
  top: 50%;
  right: 16px;
  transform: translateY(-50%);
  width: 8px;
  height: 8px;
  background: #c41217;
  border-radius: 50%;
}

.dropdown-footer {
  padding: 12px 20px;
  border-top: 1px solid #efefef;
  background: #fafafa;
}

.view-all-link {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px;
  color: #c41217;
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  border-radius: 8px;
  transition: background 0.2s;
}

.mobile-notification-link {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 999px;
  text-decoration: none;
  color: #1d1d1d;
  font-size: 14px;
  font-weight: 500;
  position: relative;
}

.mobile-notification-link i {
  font-size: 14px;
  color: #666;
}

.mobile-notification-link:hover {
  background: #f5f5f5;
}

.mobile-notification-link .badge {
  position: static;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 50%;
  background: #c41217;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
}

@media (max-width: 1400px) {
  .notification-desktop {
    display: none;
  }
}

@media (min-width: 1401px) {
  .mobile-notification-link {
    display: none;
  }
}

.view-all-link:hover {
  background: #fff5f5;
}

.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
